import type { NumberInputRootProps } from '@chakra-ui/react'
import { Box } from '@chakra-ui/react'
import { HStack, Spacer, Stack, Text } from '@chakra-ui/react'
import debounce from 'lodash/debounce'
import type { ChangeEventHandler, FC, FocusEventHandler } from 'react'
import { useState } from 'react'
import { useCallback } from 'react'

import { NumberInputField, NumberInputLabel, NumberInputRoot } from '@app/components/ui/number-input'
import cardFontSize from '@app/shared/cards/cardFontSize'
import CardSection from '@app/shared/cards/components/cardSection'
import { useStore } from '@app/store'
import type { CardSize, DomainMetric, DomainNode } from '@app/types'
import { formatTotal } from '@app/utils/metricHelpers'
import type { Metric } from '@graphql/types'

type Props = {
  node: DomainNode
  size: CardSize
}

type InputProps = NumberInputRootProps &
  Pick<Metric, 'displayFormat' | 'redecimalPercentages' | 'maximumDisplayPrecision'> & {
    label: string
    size: CardSize
  }

type ToDecimal = (number: number, metric: DomainMetric) => number
const toDecimal: ToDecimal = (number, metric) => {
  if (metric?.displayFormat !== 'percent' || !metric?.redecimalPercentages) {
    return number
  }

  return number / 100
}

const Input: FC<InputProps> = ({
  label,
  value,
  readOnly,
  size,
  displayFormat,
  redecimalPercentages,
  maximumDisplayPrecision,
  onClick,
  onBlur,
  ...rest
}) => {
  const [focused, setFocused] = useState(false)

  const handleOnFocus = async (e) => {
    setFocused(true)
    onClick?.(e)
  }

  const handleBlur = async (e) => {
    setFocused(false)
    onBlur?.(e)
  }

  const fontSize = cardFontSize('md', size)
  const formatted = formatTotal(Number(value), displayFormat, {
    redecimalPercentages,
    maximumDisplayPrecision,
    minimumDisplayPrecision: 0
  })

  return (
    <NumberInputRoot
      steppers={false}
      defaultValue={formatted?.toString() || '0'}
      onClick={handleOnFocus}
      onBlur={handleBlur}
      {...rest}
    >
      <HStack>
        <NumberInputLabel fontSize={fontSize}>{label}</NumberInputLabel>
        <Spacer />
        {readOnly || !focused ? (
          <Box
            borderColor="fg.subtle"
            borderStyle="solid"
            borderWidth={readOnly ? '0px' : '1px'}
            cursor={readOnly ? 'default' : 'pointer'}
          >
            <Text fontSize={fontSize}>{formatted}</Text>
          </Box>
        ) : (
          <NumberInputField autoFocus fontSize={fontSize} w="15ch" />
        )}
      </HStack>
    </NumberInputRoot>
  )
}

const SimMetricInputs: FC<Props> = ({ node, size }) => {
  const metric: DomainMetric = node?.data
  const updateNode = useStore.use.updateNode()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const doUpdate = useCallback(
    debounce((update: Partial<DomainNode>) => {
      updateNode(node.strategyId, node.id, update)
    }, 150),
    [updateNode, node.id, node.strategyId]
  )

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      e.preventDefault()
      e.stopPropagation()
      const value = parseFloat(e.target.value)
      doUpdate({ simBaseline: toDecimal(value, metric) })
    },
    [doUpdate, metric]
  )

  const onBlur: FocusEventHandler<HTMLInputElement> = useCallback(
    async (e) => {
      onChange(e)

      if (isNaN(parseFloat(e.target.value))) {
        e.target.value = '0'
      }
    },
    [onChange]
  )

  if (!metric) {
    return null
  }

  const { calculated, displayFormat } = metric

  // simTarget is a readonly Input, only for styling convenience

  return (
    <CardSection size={size}>
      <Stack>
        <Input
          label="Baseline:"
          name="simBaseline"
          displayFormat={displayFormat}
          value={node.simBaseline?.toString()}
          size={size}
          readOnly={calculated}
          onChange={onChange}
          onBlur={onBlur}
          redecimalPercentages={metric.redecimalPercentages}
          maximumDisplayPrecision={metric.maximumDisplayPrecision}
        />
        <Input
          label="Target:"
          displayFormat={displayFormat}
          value={node.simTarget?.toString()}
          size={size}
          readOnly
          onChange={onChange}
          onBlur={onBlur}
          redecimalPercentages={metric.redecimalPercentages}
          maximumDisplayPrecision={metric.maximumDisplayPrecision}
        />
      </Stack>
    </CardSection>
  )
}

SimMetricInputs.displayName = 'SimMetricInputs'

export default SimMetricInputs
